// OmniSearch — F*-verified batch scoring via WASM
//
// Scoring logic (tokenization, multi-token AND, distance threshold) runs
// entirely in F*-extracted WASM. TypeScript handles only rendering + DOM glue.

import escapeHtml from "escape-html";

import { loadEmscriptenFactory } from "./emscripten-loader";
// Wire-form category lives in `domain/ui/omnisearch.dox` (`OmnisearchCategory`)
// so the Elixir loader (`Operation.Search.load_category/2`) and this scorer +
// renderer share one source. A hand-rolled string-literal union here used to
// drift (TS had `issue`; Elixir didn't), so the dox-emitted alias is now the
// single declaration.
//
// `allOmnisearchCategories` is the dox-emitted Set of every union constructor
// in canonical declaration order — used to build the TABS array below so a
// new category in `omnisearch.dox` automatically picks up a tab without a
// parallel hand-rolled list drifting out of sync.
import type { OmnisearchCategory } from "../../.dox/ui";
import { allOmnisearchCategories } from "../../.dox/ui";

interface EmscriptenModule {
  readonly cwrap: (
    name: string,
    ret: string | null,
    args: readonly string[],
  ) => (...xs: readonly unknown[]) => unknown;
  readonly _malloc: (size: number) => number;
  readonly _free: (ptr: number) => void;
  readonly _score_items: (
    query: number,
    query_len: number,
    titles: number,
    titles_len: number,
    n_items: number,
    out_scores: number,
    out_positions: number,
  ) => void;
  readonly _fuzzy_substring_distance_wasm: (
    query: number,
    qlen: number,
    text: number,
    tlen: number,
  ) => number;
  readonly _fuzzy_match_position_wasm: (
    query: number,
    qlen: number,
    text: number,
    tlen: number,
  ) => number;
  readonly HEAPU8: Uint8Array;
  readonly HEAPU32: Uint32Array;
  readonly stringToUTF8: (
    str: string,
    outPtr: number,
    maxBytes: number,
  ) => void;
  readonly lengthBytesUTF8: (str: string) => number;
}

// ---------------------------------------------------------------------------
// Domain types — all readonly
// ---------------------------------------------------------------------------

interface SearchItem {
  readonly category: OmnisearchCategory;
  readonly title: string;
  readonly subtitle: string | null;
  readonly url: string;
}

// Generic over the scored item so external callers (e.g. the branch
// picker) can score their OWN item shapes through the same WASM engine
// without unsafe casts — the engine only ever reads `title`. Defaults to
// `SearchItem` so omnisearch's own call sites are unchanged.
interface ScoredResult<T = SearchItem> {
  readonly item: T;
  readonly titleHtml: string;
  readonly score: number;
}

interface TabDef {
  readonly key: OmnisearchCategory | null;
  readonly label: string;
}

interface CategoryMeta {
  readonly label: string;
  readonly icon: string; // nerdfont codepoint
  readonly color: string; // Catppuccin hex color
  readonly bg: string; // Tailwind bg class for tabs
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_DISTANCE = 2;

// Exhaustive over the dox-emitted `OmnisearchCategory` union — adding a
// member there forces a TS error here until the meta entry is filled in.
export const CATEGORY_META: Readonly<Record<OmnisearchCategory, CategoryMeta>> =
  {
    repo: {
      label: "Repos",
      icon: "\uf1d3",
      color: "text-blue",
      bg: "bg-blue/20 text-blue",
    },
    issue: {
      label: "Issues",
      icon: "\uf06a",
      color: "text-yellow",
      bg: "bg-yellow/20 text-yellow",
    },
    pr: {
      label: "PRs",
      icon: "\uf46a",
      // Theme-slot mapping like every other category: the theme system has
      // no `purple` var, so text-purple/bg-purple generated no CSS at all.
      // accent-secondary (mauve) is its purple-family slot in all themes.
      color: "text-mauve",
      bg: "bg-mauve/20 text-mauve",
    },
    lane: {
      label: "Lanes",
      icon: "\uf126",
      color: "text-teal",
      bg: "bg-teal/20 text-teal",
    },
    user: {
      label: "Users",
      icon: "\uf007",
      color: "text-green",
      bg: "bg-green/20 text-green",
    },
    group: {
      label: "Groups",
      icon: "\uf0c0",
      color: "text-mauve",
      bg: "bg-mauve/20 text-mauve",
    },
    nav: {
      label: "Pages",
      icon: "\uf0c9",
      color: "text-text-muted",
      bg: "bg-surface text-text-muted",
    },
  };

// Labels are the user-facing display strings for each dox-defined
// category. Adding a new constructor in `domain/ui/omnisearch.dox`
// surfaces here as a compile error (`Property 'foo' is missing in
// type CategoryLabel`) instead of a silently-skipped tab — the Record
// type is exhaustive over `OmnisearchCategory`.
const CATEGORY_LABELS: Record<OmnisearchCategory, string> = {
  nav: "Pages",
  repo: "Repos",
  issue: "Issues",
  pr: "PRs",
  lane: "Lanes",
  user: "Users",
  group: "Groups",
};

const TABS: readonly TabDef[] = [
  { key: null, label: "All" },
  // Driven by `allOmnisearchCategories` so a new dox category lights
  // up as a tab automatically. `Set` iteration preserves dox
  // declaration order (paradox emits constructors in source order
  // and `new Set([...])` preserves insertion order per ES2015).
  ...Array.from(allOmnisearchCategories).map((key) => ({
    key,
    label: CATEGORY_LABELS[key],
  })),
];

// ---------------------------------------------------------------------------
// WASM singleton
// ---------------------------------------------------------------------------

let _wasm: EmscriptenModule | null = null;

const initWasm = async (): Promise<void> => {
  try {
    const createTextSearch = await loadEmscriptenFactory<EmscriptenModule>(
      "createTextSearch",
      "/assets/wasm/textsearch.js",
    );

    _wasm = await createTextSearch({
      locateFile: (p: string) => `/assets/wasm/${p}`,
    });
  } catch (e) {
    console.error("[OmniSearch] WASM load failed:", e);
  }
};

// ---------------------------------------------------------------------------
// Pure string helpers
// ---------------------------------------------------------------------------

export const tokenize = (s: string): readonly string[] =>
  s.split(/\s+/).filter((t) => t.length > 0);

// ---------------------------------------------------------------------------
// WASM batch scoring — one call scores all items
// Replaces: scoreToken, scoreTitle, search loop (all now in F*)
// ---------------------------------------------------------------------------

// COUPLING: must match TextSearchWasm.fst max_tokens (16).
// A mismatch causes out_positions buffer overruns in WASM.
const MAX_TOKENS = 16;

interface BatchResult {
  readonly score: number;
  readonly positions: readonly number[]; // match position per token
}

const batchScoreWasm = (
  wasm: EmscriptenModule,
  // Only `title` is read here — keep the constraint minimal so the
  // scorer works for any titled item, not just `SearchItem`.
  items: readonly { readonly title: string }[],
  query: string,
  numTokens: number,
): readonly BatchResult[] => {
  const n = items.length;
  if (n === 0) return [];

  const qBytes = wasm.lengthBytesUTF8(query);
  const qPtr = wasm._malloc(qBytes + 1);
  wasm.stringToUTF8(query, qPtr, qBytes + 1);

  let totalLen = 0;
  for (const item of items) totalLen += wasm.lengthBytesUTF8(item.title) + 1;
  const titlesPtr = wasm._malloc(totalLen);
  let offset = 0;
  for (const item of items) {
    const len = wasm.lengthBytesUTF8(item.title);
    wasm.stringToUTF8(item.title, titlesPtr + offset, len + 1);
    offset += len + 1;
  }

  const scoresPtr = wasm._malloc(n * 4);
  const posPtr = wasm._malloc(n * MAX_TOKENS * 4);

  try {
    wasm._score_items(qPtr, qBytes, titlesPtr, totalLen, n, scoresPtr, posPtr);

    const scoresOff = scoresPtr >> 2;
    const posOff = posPtr >> 2;
    const results: BatchResult[] = [];
    for (let i = 0; i < n; i++) {
      const score = wasm.HEAPU32[scoresOff + i]!;
      const positions: number[] = [];
      // Only read positions when score indicates a match — unmatched items
      // (score === 999) may have uninitialized position data in the WASM heap.
      if (score < 999) {
        for (let t = 0; t < numTokens; t++)
          positions.push(wasm.HEAPU32[posOff + i * MAX_TOKENS + t]!);
      }
      results.push({ score, positions });
    }
    return results;
  } finally {
    wasm._free(qPtr);
    wasm._free(titlesPtr);
    wasm._free(scoresPtr);
    wasm._free(posPtr);
  }
};

// ---------------------------------------------------------------------------
// Highlight rendering — bold mask from per-token positions
// ---------------------------------------------------------------------------

export const renderBoldFromPositions = (
  title: string,
  positions: readonly number[],
  tokenLens: readonly number[],
): string => {
  const mask = new Uint8Array(title.length);
  for (let t = 0; t < positions.length; t++)
    for (
      let i = positions[t]!;
      i < positions[t]! + tokenLens[t]! && i < title.length;
      i++
    )
      mask[i] = 1;
  let html = "",
    open = false;
  for (let i = 0; i < title.length; i++) {
    if (mask[i] && !open) {
      html += '<strong class="text-accent font-semibold">';
      open = true;
    }
    if (!mask[i] && open) {
      html += "</strong>";
      open = false;
    }
    html += escapeHtml(title[i]!);
  }
  return open ? html + "</strong>" : html;
};

// ---------------------------------------------------------------------------
// Search — WASM batch scoring
// ---------------------------------------------------------------------------

const search = <T extends { readonly title: string }>(
  items: readonly T[],
  query: string,
  max: number = 20,
): readonly ScoredResult<T>[] => {
  const q = query.trim();
  if (q.length === 0) return [];
  if (!_wasm) {
    console.warn(
      "[OmniSearch] WASM module not loaded — returning empty results for query:",
      q,
    );
    return [];
  }

  const tokens = tokenize(q);
  const tokenLens = tokens.map((t) => t.length);
  const maxTotal = MAX_DISTANCE * tokens.length;

  const batch = batchScoreWasm(_wasm, items, q, tokens.length);
  return items
    .flatMap((item, i): ScoredResult<T>[] => {
      const { score, positions } = batch[i]!;
      if (score > maxTotal) return [];
      return [
        {
          item,
          titleHtml: renderBoldFromPositions(item.title, positions, tokenLens),
          score,
        },
      ];
    })
    .sort((a, b) => a.score - b.score)
    .slice(0, max);
};

export const filterByTab = (
  results: readonly ScoredResult[],
  tab: OmnisearchCategory | null,
): readonly ScoredResult[] =>
  tab ? results.filter((r) => r.item.category === tab) : results;

// ---------------------------------------------------------------------------
// Rendering — pure string → string (stays in TS)
// ---------------------------------------------------------------------------

const renderResult = (r: ScoredResult): string => {
  // CATEGORY_META is a `Record<OmnisearchCategory, …>` (exhaustive over the
  // dox-emitted union), so at the type level the lookup is total — but the
  // server can in principle ship a category that doesn't validate (e.g. an
  // older deploy mid-rollout, or a property-test feeding raw strings). The
  // `as Partial<…>` widens the access type so the `meta &&` guard isn't
  // flagged as unnecessary by the linter, and a bad wire surfaces as an
  // unbadged result rather than `Cannot read properties of undefined`.
  const meta = (CATEGORY_META as Partial<Record<string, CategoryMeta>>)[
    r.item.category
  ];
  const badge = meta
    ? `<span class="nf-icon flex-shrink-0 ${meta.color}">${meta.icon}</span>`
    : "";
  const sub = r.item.subtitle
    ? `<p class="text-text-muted truncate">${escapeHtml(r.item.subtitle)}</p>`
    : "";
  return (
    `<a data-search-result data-url="${escapeHtml(r.item.url)}"` +
    ` class="flex items-center gap-3 px-3 py-1.5 hover:bg-surface cursor-pointer">` +
    badge +
    `<div class="flex-1 min-w-0"><p class="text-text truncate">${r.titleHtml}</p>${sub}</div></a>`
  );
};

const renderResults = (results: readonly ScoredResult[]): string =>
  results.length === 0
    ? '<div class="px-4 py-4 text-center text-text-muted">No results found</div>'
    : results.map(renderResult).join("");

const renderTabs = (
  active: OmnisearchCategory | null,
  all: readonly ScoredResult[],
): string => {
  const tabHtml = TABS.map((tab) => {
    const n = tab.key
      ? all.filter((r) => r.item.category === tab.key).length
      : all.length;
    const cls =
      tab.key === active
        ? "bg-surface text-text font-medium"
        : "text-text-muted hover:text-text hover:bg-surface/50";
    const count =
      n > 0 && tab.key ? ` <span class="text-text-muted">${n}</span>` : "";
    return `<button data-search-tab="${tab.key || ""}" class="px-2.5 py-1 rounded text-xs transition-colors whitespace-nowrap ${cls}">${escapeHtml(tab.label)}${count}</button>`;
  }).join("");
  return `<div class="flex gap-1 px-3 py-2 border-b border-border overflow-x-auto">${tabHtml}</div>`;
};

const renderEmpty = (): string =>
  '<div class="px-4 py-4 text-text-muted text-center">Start typing to search across repos, issues, PRs, users, and groups.</div>';

// ---------------------------------------------------------------------------
// Module initialization + export
// ---------------------------------------------------------------------------

let _items: readonly SearchItem[] = [];
void initWasm();

export const load = (items: readonly SearchItem[]): void => {
  _items = items;
};
export const searchItems = (query: string): readonly ScoredResult[] =>
  search(_items, query);

// Stateless generic scoring for external callers that hold their own item
// list (e.g. the branch picker). Same WASM engine, no module state, no
// casts — the caller's item type flows straight through `ScoredResult<T>`.
export const scoreItems = <T extends { readonly title: string }>(
  items: readonly T[],
  query: string,
  max = 50,
): readonly ScoredResult<T>[] => search(items, query, max);

export { renderResult, renderResults, renderTabs, renderEmpty };
export type { SearchItem, ScoredResult };
